import { useState, useEffect, useMemo } from "react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Checkbox } from "@/frontend/components/ui/checkbox";
import { Badge } from "@/frontend/components/ui/badge";
import { Calendar } from "@/frontend/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/frontend/components/ui/popover";
import { Search, X, Calendar as CalendarIcon, Filter } from "lucide-react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useSession } from "@/frontend/lib/auth";
import { useOrgStore } from "@/frontend/stores/orgStore";
import { cn } from "@/frontend/lib/utils";
import { useChatOptions } from "@/frontend/hooks/useChatOptions";

interface Contact {
  senderName: string;
  count: number; // number of messages from this contact
}

export interface ChatFilters {
  dateRange?: { start: Date; end: Date };
  conversationIds?: string[];
  senderNames?: string[];
}

interface ChatFilterPanelProps {
  filters: ChatFilters;
  onChange: (filters: ChatFilters) => void;
  onClear: () => void;
}

export default function ChatFilterPanel({
  filters,
  onChange,
  onClear,
}: ChatFilterPanelProps) {
  const { data: session } = useSession();
  const currentOrgId = useOrgStore((state) => state.currentOrgId);

  // Local state
  const [conversationSearch, setConversationSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Date range state
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [tempDateRange, setTempDateRange] = useState<DateRange | undefined>(
    filters.dateRange ? { from: filters.dateRange.start, to: filters.dateRange.end } : undefined
  );

  // Sync temp date range with filters
  useEffect(() => {
    setTempDateRange(
      filters.dateRange ? { from: filters.dateRange.start, to: filters.dateRange.end } : undefined
    );
  }, [filters.dateRange]);

  // Searched on the server, a page at a time
  const {
    options: conversations,
    isLoading: conversationsLoading,
    hasMore: hasMoreConversations,
    isLoadingMore: isLoadingMoreConversations,
    loadMore: loadMoreConversations,
  } = useChatOptions({ organizationId: currentOrgId || undefined, search: conversationSearch });

  // Load contacts (unique senders from messages)
  useEffect(() => {
    const loadContacts = async () => {
      if (!currentOrgId || !session?.user?.id) return;

      setContactsLoading(true);
      try {
        const url = `/api/chat/contacts?organizationId=${encodeURIComponent(currentOrgId)}`;
        const response = await fetch(url);

        if (response.ok) {
          const data = (await response.json()) as { contacts: Contact[] };
          setAllContacts(data.contacts || []);
        }
      } catch (error) {
        console.error("Failed to load contacts:", error);
        // Don't show error toast as this is a nice-to-have feature
      } finally {
        setContactsLoading(false);
      }
    };

    loadContacts();
  }, [currentOrgId, session?.user?.id]);

  const filteredAndGroupedConversations = useMemo(() => ({
    dms: conversations.filter((conv) => conv.conversationType === "dm"),
    groups: conversations.filter((conv) => conv.conversationType === "group"),
  }), [conversations]);

  // Filter contacts
  const filteredContacts = useMemo(() => {
    if (!contactSearch) return allContacts;
    const searchLower = contactSearch.toLowerCase();
    return allContacts.filter((contact) =>
      contact.senderName.toLowerCase().includes(searchLower)
    );
  }, [allContacts, contactSearch]);

  const handleConversationToggle = (conversationId: string, checked: boolean) => {
    const currentIds = filters.conversationIds || [];
    const newIds = checked
      ? [...currentIds, conversationId]
      : currentIds.filter((id) => id !== conversationId);

    onChange({
      ...filters,
      conversationIds: newIds.length > 0 ? newIds : undefined,
    });
  };

  const handleContactToggle = (senderName: string, checked: boolean) => {
    const currentNames = filters.senderNames || [];
    const newNames = checked
      ? [...currentNames, senderName]
      : currentNames.filter((name) => name !== senderName);

    onChange({
      ...filters,
      senderNames: newNames.length > 0 ? newNames : undefined,
    });
  };

  const handleDateRangeDone = () => {
    if (tempDateRange?.from && tempDateRange?.to) {
      onChange({
        ...filters,
        dateRange: { start: tempDateRange.from, end: tempDateRange.to },
      });
    }
    setDateRangeOpen(false);
  };

  const handleDateRangeCancel = () => {
    setTempDateRange(
      filters.dateRange ? { from: filters.dateRange.start, to: filters.dateRange.end } : undefined
    );
    setDateRangeOpen(false);
  };

  const clearDateRange = () => {
    setTempDateRange(undefined);
    const newFilters = { ...filters };
    delete newFilters.dateRange;
    onChange(newFilters);
  };

  const hasActiveFilters =
    filters.dateRange ||
    (filters.conversationIds && filters.conversationIds.length > 0) ||
    (filters.senderNames && filters.senderNames.length > 0);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-card">
      {/* Header - aligned with input field height */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-border flex items-center h-[52px]">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <h3
            className="text-base font-semibold"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            Advanced Filters
          </h3>
        </div>
      </div>

      {/* Filters Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Date Range Filter */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Date Range</Label>
          <Popover open={dateRangeOpen} onOpenChange={setDateRangeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !filters.dateRange && "text-muted-foreground"
                )}
                size="sm"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateRange?.start && filters.dateRange?.end ? (
                  <>
                    {format(filters.dateRange.start, "LLL dd, y")} -{" "}
                    {format(filters.dateRange.end, "LLL dd, y")}
                  </>
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-auto p-0 !z-50"
              align="start"
              sideOffset={10}
              style={{ zIndex: 50 }}
              onInteractOutside={(e) => {
                // Prevent auto-closing when interacting with calendar
                e.preventDefault();
              }}
            >
              <div className="flex flex-col bg-white dark:bg-card border rounded-lg shadow-lg">
                <Calendar
                  mode="range"
                  defaultMonth={tempDateRange?.from || new Date()}
                  selected={tempDateRange}
                  onSelect={setTempDateRange}
                  numberOfMonths={2}
                  className="rounded-t-lg"
                  classNames={{
                    day: "h-9 w-9",
                    weekday: "h-9 w-9",
                  }}
                />
                <div className="flex justify-end gap-2 p-3 border-t">
                  <Button variant="outline" onClick={handleDateRangeCancel} size="sm">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDateRangeDone}
                    disabled={!tempDateRange?.from || !tempDateRange?.to}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-gray-700"
                    size="sm"
                  >
                    Done
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          {filters.dateRange && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearDateRange}
              className="w-full text-xs"
            >
              <X className="h-3 w-3 mr-1" />
              Clear date range
            </Button>
          )}
        </div>

        {/* Conversations Filter */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Conversations</Label>
            <Badge variant="secondary" className="text-xs">
              {filters.conversationIds?.length || 0} selected
            </Badge>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <Input
              placeholder="Search conversations..."
              value={conversationSearch}
              onChange={(e) => setConversationSearch(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
            {conversationSearch && (
              <button
                onClick={() => setConversationSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Conversations list */}
          <div className="max-h-64 overflow-y-auto border rounded-lg p-2">
            {conversationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : conversations.length === 0 && !conversationSearch ? (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                No conversations found
              </p>
            ) : (
              <div className="space-y-2">
                {/* DMs */}
                {filteredAndGroupedConversations.dms.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2 px-1">
                      DMs ({filteredAndGroupedConversations.dms.length})
                    </h4>
                    <div className="space-y-1">
                      {filteredAndGroupedConversations.dms.map((conv) => (
                        <div
                          key={conv.conversationId}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-md"
                        >
                          <Checkbox
                            id={`chat-conv-${conv.conversationId}`}
                            checked={filters.conversationIds?.includes(conv.conversationId)}
                            onCheckedChange={(checked) =>
                              handleConversationToggle(conv.conversationId, checked as boolean)
                            }
                          />
                          <label
                            htmlFor={`chat-conv-${conv.conversationId}`}
                            className="text-sm cursor-pointer flex-1 truncate"
                          >
                            {conv.conversationName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Groups */}
                {filteredAndGroupedConversations.groups.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2 px-1">
                      Groups ({filteredAndGroupedConversations.groups.length})
                    </h4>
                    <div className="space-y-1">
                      {filteredAndGroupedConversations.groups.map((conv) => (
                        <div
                          key={conv.conversationId}
                          className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-md"
                        >
                          <Checkbox
                            id={`chat-conv-${conv.conversationId}`}
                            checked={filters.conversationIds?.includes(conv.conversationId)}
                            onCheckedChange={(checked) =>
                              handleConversationToggle(conv.conversationId, checked as boolean)
                            }
                          />
                          <label
                            htmlFor={`chat-conv-${conv.conversationId}`}
                            className="text-sm cursor-pointer flex-1 truncate"
                          >
                            {conv.conversationName}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No results */}
                {filteredAndGroupedConversations.dms.length === 0 &&
                  filteredAndGroupedConversations.groups.length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No matches
                    </p>
                  )}

                {hasMoreConversations && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs"
                    disabled={isLoadingMoreConversations}
                    onClick={loadMoreConversations}
                  >
                    {isLoadingMoreConversations ? "Loading..." : "Load more"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Contacts Filter */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Contacts</Label>
            <Badge variant="secondary" className="text-xs">
              {filters.senderNames?.length || 0} selected
            </Badge>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
            <Input
              placeholder="Search contacts..."
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              className="pl-10 h-9 text-sm"
            />
            {contactSearch && (
              <button
                onClick={() => setContactSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Contacts list */}
          <div className="max-h-64 overflow-y-auto border rounded-lg p-2">
            {contactsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-gray-100"></div>
              </div>
            ) : allContacts.length === 0 ? (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                No contacts found
              </p>
            ) : (
              <div className="space-y-1">
                {filteredContacts.map((contact) => (
                  <div
                    key={contact.senderName}
                    className="flex items-center justify-between gap-2 p-2 hover:bg-gray-50 dark:hover:bg-white/5 rounded-md"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Checkbox
                        id={`contact-${contact.senderName}`}
                        checked={filters.senderNames?.includes(contact.senderName)}
                        onCheckedChange={(checked) =>
                          handleContactToggle(contact.senderName, checked as boolean)
                        }
                      />
                      <label
                        htmlFor={`contact-${contact.senderName}`}
                        className="text-sm cursor-pointer flex-1 truncate"
                      >
                        {contact.senderName}
                      </label>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">
                      {contact.count}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer Actions - aligned with chat footer */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-border">
        <Button
          onClick={onClear}
          variant="outline"
          className="w-full"
          size="sm"
          disabled={!hasActiveFilters}
        >
          <X className="w-4 h-4 mr-2" />
          Clear All Filters
        </Button>
        {hasActiveFilters && (
          <p className="text-xs text-center text-muted-foreground mt-3">
            Filters will apply to your next questions
          </p>
        )}
      </div>
    </div>
  );
}
